import { useEffect, useMemo, useState, Fragment } from "react";
import { fetchSheetViaProxy } from "../../../../lib/sheets";

// === Archiv-Datei ===
const ARCHIVE_SHEET_ID = "1YX4CzZlUxNIheMi_BKSoJivlo9M3g9cpcdPmqwtWroQ";

// Falls der Teams-Tab in derselben Archiv-Datei liegt, so lassen.
// Falls er in einer anderen Datei liegt, hier eine andere ID eintragen.
const ARCHIVE_TEAMS_SHEET_ID = ARCHIVE_SHEET_ID;

const RANGE = "archive_s1_standings_team!A1:AI100";
const TEAMS_RANGE = "archive_teams!E1:G1000";

// "A" -> 1, "Z" -> 26, "AA" -> 27, ...
const colIndex = (letter) => {
    let n = 0;
    for (let i = 0; i < letter.length; i++) n = n * 26 + (letter.charCodeAt(i) - 64);
    return n;
};

const LETTER_AA = colIndex("AA");
const LETTER_M  = colIndex("M");
const LETTER_X  = colIndex("X");
const LETTER_Y  = colIndex("Y");

const isNumeric = (v) => v !== null && v !== "" && !isNaN(Number(v));
const norm = (s) => String(s ?? "").trim().toLowerCase();

function computeTop3ByColumn(rowsAsArrays, startCol, endCol) {
    const map = new Map();
    for (let c = startCol; c <= endCol; c++) {
        const nums = rowsAsArrays
            .map(arr => arr[c - 1])
            .filter(v => v !== null && v !== undefined && v !== "")
            .map(v => Number(v))
            .filter(v => !Number.isNaN(v));

        if (!nums.length) {
            map.set(c, null);
            continue;
        }

        const uniqDesc = Array.from(new Set(nums)).sort((a, b) => b - a);
        map.set(c, {
            gold: uniqDesc[0] ?? null,
            silver: uniqDesc[1] ?? null,
            bronze: uniqDesc[2] ?? null,
        });
    }
    return map;
}

function toRowsAsArrays(sheetData) {
    const headers = sheetData?.header || [];
    const rowsObj = sheetData?.rows || [];
    return rowsObj.map((r) => headers.map((h) => r[h]));
}

export default function ArchiveSeason1Team() {
    const [data, setData] = useState({ header: [], rows: [] });
    const [teamsData, setTeamsData] = useState({ header: [], rows: [] });

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [noData, setNoData] = useState(false);

    const [openTeams, setOpenTeams] = useState({});

    const toggleTeam = (teamName) => {
        const key = norm(teamName);
        setOpenTeams((prev) => ({ ...prev, [key]: !prev[key] }));
    };

    useEffect(() => {
        (async () => {
            try {
                const [resStandings, resTeams] = await Promise.all([
                    fetchSheetViaProxy({ range: RANGE, sheetId: ARCHIVE_SHEET_ID }),
                    fetchSheetViaProxy({ range: TEAMS_RANGE, sheetId: ARCHIVE_TEAMS_SHEET_ID }),
                ]);

                setData(resStandings);
                setTeamsData(resTeams);
            } catch (e) {
                const msg = e?.message || String(e);
                if (msg.includes("Unable to parse range")) {
                    setNoData(true);
                } else {
                    setError(msg);
                }
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    const {
        visibleHeaders,
        idxTeamLogoUrl,
        rowsAsArrays,
        top3PerRaceColumn,
        completedRuns,
        teamToDrivers,
        visibleColNumbers,
    } = useMemo(() => {
        const headers = data.header || [];

        const visibleHeaders = headers.slice(0, LETTER_AA);
        const idxTeamLogoUrl = colIndex("AB") - 1;

        const rowsAsArrays = (data.rows || []).map((r) => headers.map((h) => r[h]));
        const top3PerRaceColumn = computeTop3ByColumn(rowsAsArrays, LETTER_M, LETTER_X);

        let completedRuns = 0;
        for (let c = LETTER_M; c <= LETTER_X; c++) {
            const anyNumberInCol = rowsAsArrays.some(row => {
                const v = row[c - 1];
                return v !== null && v !== "" && !Number.isNaN(Number(v));
            });
            if (anyNumberInCol) completedRuns++;
        }

        const visibleColNumbers = visibleHeaders
            .map((_, i) => i + 1)
            .filter((colNumber) => colNumber !== 5);

        const teamsArr = toRowsAsArrays(teamsData);
        const teamToDrivers = new Map();

        for (const row of teamsArr) {
            const team = String(row[0] ?? "").trim();      // E
            const driver = String(row[1] ?? "").trim();    // F
            const pointsRaw = row[2];                      // G
            const points = (pointsRaw == null || String(pointsRaw).trim() === "")
                ? ""
                : String(pointsRaw).trim();

            if (!team || !driver) continue;
            if (norm(team) === "team" && norm(driver).includes("fahrer")) continue;

            const k = norm(team);
            if (!teamToDrivers.has(k)) teamToDrivers.set(k, []);
            teamToDrivers.get(k).push({ name: driver, points });
        }

        for (const [k, arr] of teamToDrivers.entries()) {
            const seen = new Set();
            teamToDrivers.set(
                k,
                arr.filter((d) => {
                    const dk = norm(d.name);
                    if (!dk || seen.has(dk)) return false;
                    seen.add(dk);
                    return true;
                })
            );
        }

        return {
            visibleHeaders,
            idxTeamLogoUrl,
            rowsAsArrays,
            top3PerRaceColumn,
            completedRuns,
            teamToDrivers,
            visibleColNumbers,
        };
    }, [data, teamsData]);

    if (loading) return <div className="card"><p>Lade Archiv-Standings...</p></div>;

    if (noData) {
        return (
            <div className="card">
                <h2>Archiv Season 1 <span className="pill pill--team">TEAMS</span></h2>
                <p className="muted">
                    Für diese Archivansicht sind aktuell noch keine Teamdaten vorhanden.
                </p>
            </div>
        );
    }

    if (error) {
        return <div className="card"><p style={{ color:"#f55" }}>Fehler: {error}</p></div>;
    }

    const renderDriverRow = (driver, key) => {
        const name = typeof driver === "string" ? driver : (driver?.name ?? "");
        const points = typeof driver === "string" ? "" : (driver?.points ?? "");

        const label = points && String(points).trim() !== ""
            ? `• ${name} - ${String(points).trim()} Punkte`
            : `• ${name}`;

        return (
            <tr key={key} className="team-driver-row">
                {visibleColNumbers.map((colNumber, idx) => {
                    if (colNumber === 4) {
                        return (
                            <td key={idx} className="align-left">
                                <span className="driver-name">{label}</span>
                            </td>
                        );
                    }

                    if (colNumber === 3) return <td key={idx} className="icon" />;

                    return <td key={idx} />;
                })}
            </tr>
        );
    };

    return (
        <>
            <div className="card">
                <h2>Archiv Season 1 <span className="pill pill--team">TEAMS</span></h2>
                <p className="muted">
                    Teamwertung der Season 1 nach Abschluss des {completedRuns}. Laufes. <br/>
                    Die Teamwertung wird aus der Gesamtwertung des Laufes gebildet mit den Top 15 Fahrern.
                    Pro Team können pro Lauf jedoch maximal zwei Fahrer gewertet werden.
                </p>
            </div>

            <div className="fancyTable">
                <table className="tbl">
                    <thead>
                    <tr>
                        {visibleHeaders.map((h, i) => {
                            const colNumber = i + 1;
                            if (colNumber === 5) return null;

                            const label =
                                colNumber === 1 ? "Pos" :
                                    colNumber === 2 ? "Δ" :
                                        colNumber === 6 ? "Pts" :
                                            colNumber === 7 ? "Diff" :
                                                colNumber === 8 ? "Inc" :
                                                    colNumber === 9 ? "R" :
                                                        colNumber === 10 ? "W" :
                                                            colNumber === 11 ? "P" :
                                                                colNumber === 12 ? "DNF" :
                                                                    h;

                            const isRace = colNumber >= LETTER_M && colNumber <= LETTER_X;
                            const raceLeft = colNumber === LETTER_M;
                            const raceRight = colNumber === LETTER_X;
                            const evenStripe = isRace && ((colNumber - LETTER_M) % 2 === 0);

                            const leftAligned = (colNumber === 1 || colNumber === 4 || colNumber === 5);

                            const thClass = [
                                isRace ? "race-col" : "",
                                raceLeft ? "race-left" : "",
                                raceRight ? "race-right" : "",
                                evenStripe ? "race-alt" : "",
                                (colNumber === 3) ? "icon" : "",
                                leftAligned ? "align-left" : "",
                            ].filter(Boolean).join(" ");

                            return <th key={i} className={thClass}>{label}</th>;
                        })}
                    </tr>
                    </thead>

                    <tbody>
                    {rowsAsArrays.map((arr, ri) => {
                        const teamName = String(arr[3] ?? "").trim();
                        const teamKey = norm(teamName);
                        const isOpen = !!openTeams[teamKey];
                        const drivers = teamToDrivers.get(teamKey) || [];

                        return (
                            <Fragment key={ri}>
                                <tr>
                                    {visibleHeaders.map((_, i) => {
                                        const colNumber = i + 1;
                                        if (colNumber === 5) return null;
                                        const val = arr[i];

                                        if (colNumber === 3) {
                                            const src = arr[idxTeamLogoUrl] || "";
                                            return (
                                                <td key={i} className="icon">
                                                    {src ? <img src={src} alt="Team" className="teamlogo lg" loading="lazy" /> : null}
                                                </td>
                                            );
                                        }

                                        const isRace = colNumber >= LETTER_M && colNumber <= LETTER_X;
                                        const raceLeft = colNumber === LETTER_M;
                                        const raceRight = colNumber === LETTER_X;
                                        const evenStripe = isRace && ((colNumber - LETTER_M) % 2 === 0);

                                        let tierClass = "";
                                        if (isRace && val !== "" && val !== null && val !== undefined) {
                                            const numVal = Number(val);
                                            const t3 = top3PerRaceColumn.get(colNumber);
                                            if (t3 && !Number.isNaN(numVal)) {
                                                if (numVal === t3.gold) tierClass = "race-gold";
                                                else if (numVal === t3.silver) tierClass = "race-silver";
                                                else if (numVal === t3.bronze) tierClass = "race-bronze";
                                            }
                                        }

                                        const leftAligned = (colNumber === 1 || colNumber === 4 || colNumber === 5);

                                        let deltaClass = "";
                                        if (colNumber === 2) {
                                            const n = Number(val);
                                            if (!isNaN(n)) {
                                                if (n > 0) deltaClass = "delta-up";
                                                else if (n < 0) deltaClass = "delta-down";
                                            }
                                        }

                                        const compactMetrics = (colNumber >= 6 && colNumber <= 12) ? "compact-metrics" : "";

                                        const numeric =
                                            isNumeric(val) ||
                                            (colNumber >= 6 && colNumber <= 12) ||
                                            isRace ||
                                            (colNumber >= LETTER_Y && colNumber <= LETTER_AA);

                                        const tdClass = [
                                            numeric ? "num" : "",
                                            isRace ? "race-col" : "",
                                            raceLeft ? "race-left" : "",
                                            raceRight ? "race-right" : "",
                                            evenStripe ? "race-alt" : "",
                                            tierClass,
                                            leftAligned ? "align-left" : "",
                                            (colNumber === 6) ? "points-highlight" : "",
                                            (colNumber === 1) ? "pos-highlight" : "",
                                            deltaClass,
                                            compactMetrics,
                                        ].filter(Boolean).join(" ");

                                        if (colNumber === 4) {
                                            return (
                                                <td key={i} className={tdClass}>
                                                    <button
                                                        type="button"
                                                        className="team-expand-btn"
                                                        aria-label={isOpen ? "Team einklappen" : "Team ausklappen"}
                                                        onClick={(e) => {
                                                            e.preventDefault();
                                                            e.stopPropagation();
                                                            toggleTeam(teamName);
                                                        }}
                                                    >
                                                        {isOpen ? "−" : "+"}
                                                    </button>
                                                    <span className="team-name">{val}</span>
                                                </td>
                                            );
                                        }

                                        return <td key={i} className={tdClass}>{val}</td>;
                                    })}
                                </tr>

                                {isOpen && (
                                    <>
                                        {drivers.length > 0
                                            ? drivers.map((d, di) => renderDriverRow(d, `drv-${ri}-${di}`))
                                            : renderDriverRow("keine Fahrer im Teams-Tab gefunden", `drv-empty-${ri}`)}
                                    </>
                                )}
                            </Fragment>
                        );
                    })}
                    </tbody>
                </table>
            </div>
        </>
    );
}